'use client';
// Shared client-side state so the QuickConsult bottom bar can survive route changes —
// 2026-07-23 user request: clicking a pro card inside the bar navigates to that pro's
// page, but the bar itself must stay exactly as it was (same city, same shown pros),
// not remount/reset. The trick is architectural, not a "technology": the bar is rendered
// ONCE from the persistent site layout (SiteShell.tsx, outside the routed page content),
// so React never unmounts it across a client-side navigation — only the ROUTED page
// content under it swaps. This context holds just the base data (which article seeded
// it, its category, its pro pool) that the bar needs but can only be SUPPLIED by the
// article page currently being viewed (see QuickConsultClient.tsx, now a "seeder" that
// pushes data in via seed() and renders nothing itself). All the bar's own interactive
// state (chosen city, narrowed pro list, sent flag, CTA copy variant) stays as plain
// local useState INSIDE QuickConsultBar.tsx — since that component never unmounts, that
// state persists automatically with no extra plumbing needed.
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import type { QuickConsultPro } from '@/lib/quickConsultTypes';
import type { CtaVariant } from '@/lib/ctaVariantsShared';
import type { Questionnaire } from '@/components/QuestionnaireWizard';

export type QuickConsultBase = {
  articleId?: number;
  categorySlug: string;
  categoryName: string;
  categoryNamePlural: string;
  pros: QuickConsultPro[];
  variants: CtaVariant[];
  bgAvatars: string[];
  // "🎯 שאלות לייעוץ מדויק" mode — the article's hook question + category questionnaire,
  // merged server-side (see QuickConsultWidget.tsx). Null when the category has no
  // published questionnaire yet — the toggle button hides itself in that case.
  preciseQuestionnaire: Questionnaire | null;
  skipCityQuestion: boolean;
  // Nationwide category (Founder 2026-09-22): the city is asked LAST ("באיזו עיר צריך את השירות?") — needed for the message
  // wording ("ואני מ<עיר>"), never for filtering; the bar's radius narrowing is skipped too.
  cityLast?: boolean;
  // Category×city SEO landing pages (e.g. /שמאי-מקרקעין/תל-אביב) already know the city
  // from the URL — skip asking it again, same as the old CategoryQuestionnaire behavior.
  presetCity?: { name: string; lat: number; lng: number } | null;
  // Set ONLY when seeded from a specific professional's own page (2026-08-22 fix —
  // was previously just `proId`, used purely for seedKey identity, which let the bar
  // fall through to its normal category-wide pro pool/matching and show a DIFFERENT
  // professional's name/photo than the page the visitor was actually on). When set,
  // QuickConsultBar must pin `shownPros` to ONLY this pro (never re-matched by city),
  // and the referral send must go to this pro alone (see handlePreciseSend).
  singlePro?: QuickConsultPro | null;
  // 2026-07-29 (widget unification — category/pro pages now seed this same store, not
  // just article pages): stable identity for "did the SEEDED CONTEXT actually change"
  // (QuickConsultBar's reset effect keys off this, not articleId directly). articleId
  // alone broke down once category/pro pages started seeding with articleId=undefined —
  // undefined never equals itself as a "no-op re-seed" in the old check, so every
  // category↔category or pro↔pro navigation was wrongly wiping the visitor's in-progress
  // answers. Computed once per seed() call by the caller (QuickConsultClient).
  seedKey: string;
  // ?layout= preview only (Founder 01/10/26, lib/topicLayout.ts): compact bottom-bar variant; omitted = the standard bar
  barVariant?: 'full' | 'one' | 'row' | 'pill' | 'seg' | 'sheetpill' | 'sheetfooter' | 'madlibs';
};

type QuickConsultState = QuickConsultBase & { active: boolean };

const EMPTY: QuickConsultState = {
  active: false, categorySlug: '', categoryName: '', categoryNamePlural: '', pros: [], variants: [], bgAvatars: [],
  preciseQuestionnaire: null, skipCityQuestion: false, cityLast: false, seedKey: '', singlePro: null,
};

const Ctx = createContext<{
  state: QuickConsultState;
  seed: (base: QuickConsultBase) => void;
} | null>(null);

export function QuickConsultProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<QuickConsultState>(EMPTY);

  const value = useMemo(() => ({
    state,
    seed: (base: QuickConsultBase) => setState({ ...base, active: true }),
  }), [state]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// No-op fallback (not a throw) for contexts outside the site layout — e.g. the
// /admin/article-elements demo catalog renders every block type, including
// `quick_consult`, standalone for preview purposes with no QuickConsultProvider/Bar
// mounted there. The seeder calling seed() into a void is harmless; there's simply
// nothing listening.
const NOOP = { state: EMPTY, seed: () => {} };

export function useQuickConsult() {
  const ctx = useContext(Ctx);
  return ctx ?? NOOP;
}

// CONSULT DRAFT (Founder 02/10/26, WhatsApp consult link): the visitor's answers so far (need + city), written by
// QuickConsultBar as he answers and read by ConsultFloat at tap time → lib/consultLink.mjs consultHref() → /wa/consult,
// so the consult prefill carries the quiz answers + topic + city through the ONE composer. Module-level on purpose:
// nobody re-renders on an answer; a different category's draft is never used.
export type ConsultDraft = { categorySlug: string; need?: string | null; city?: string | null };
let consultDraft: ConsultDraft | null = null;
export function setConsultDraft(d: ConsultDraft): void {
  const prev = consultDraft && consultDraft.categorySlug === d.categorySlug ? consultDraft : { categorySlug: d.categorySlug };
  consultDraft = { ...prev, ...d };
}
export function getConsultDraft(categorySlug: string): ConsultDraft | null {
  return consultDraft && consultDraft.categorySlug === categorySlug ? consultDraft : null;
}
